import axios, { AxiosInstance } from "axios";
import { env } from "../utils/env";
import { getAccessToken, triggerLogout } from "../auth/token-accessor";

const baseURL = import.meta.env.DEV ? "/api" : env.BACKEND_URL;
const httpClient = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Attach a fresh access token to every request. The Auth0 SDK renews tokens
// silently (refresh-token rotation), so this transparently handles expiry.
// A transient renewal failure rejects the request (so React Query can retry)
// rather than sending it unauthenticated into the 401-logout path, which would
// surface as a surprise logout.
httpClient.interceptors.request.use(async (config) => {
  const token = await getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

httpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token could not be renewed and the request is still unauthorized:
      // the session is genuinely gone, so log the user out cleanly.
      triggerLogout();
    }
    return Promise.reject(error);
  },
);

export const useHttpClient = (): AxiosInstance => {
  return httpClient;
};
